'use client';

import NextImageView from '@/components/ui/NextImageView/NextImageView';
import UserDesignGridSection from '@/features/gfUserDesign/components/UserDesignGridSection/UserDesignGridSection';
import { SIZE_SELECT_FROM } from '@/features/gfUserDesign/constants';
import ItemFilterModal from '@/features/gfWorks/components/ItemFilterModal/ItemFilterModal';
import WorkDetailSidebar from '@/features/gfWorks/components/WorkDetailSidebar/WorkDetailSidebar';
import WorksDetailFooterProductFirst from '@/features/gfWorks/components/WorksDetailFooterProductFirst/WorksDetailFooterProductFirst';
import type { GfWorkDetail } from '@/features/gfWorks/types';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { Product, UserDesignItem } from '@/types';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState, useTransition } from 'react';
import styles from './WorkUserDesignsPage.module.scss';

function WorkUserDesignsPage({
  work,
  initialDesigns,
  initialHasMore,
  products,
  workId,
  selectedProductId,
  initialOpenUserPrintId,
}: {
  work: GfWorkDetail;
  initialDesigns: UserDesignItem[];
  initialHasMore: boolean;
  products: Product[];
  workId: number;
  selectedProductId: number | null;
  initialOpenUserPrintId?: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [isItemFilterOpen, setIsItemFilterOpen] = useState(false);

  const selectedProduct = useMemo(
    () => (selectedProductId !== null ? products.find((p) => p.id === selectedProductId) : null),
    [selectedProductId, products],
  );

  const selectedProductIds = useMemo(
    () => (selectedProductId !== null ? [selectedProductId] : []),
    [selectedProductId],
  );

  const handleItemFilterToggle = useCallback(
    (productId: number) => {
      const params = new URLSearchParams(searchParams);
      const newProductId = selectedProductId === productId ? null : productId;

      startTransition(() => {
        if (newProductId === null) params.delete('productId');
        else params.set('productId', productId.toString());
        router.replace(`?${params.toString()}`, { scroll: false });
      });

      setIsItemFilterOpen(false);
    },
    [selectedProductId, searchParams, router],
  );

  const handleRemoveItemFilter = useCallback(() => {
    if (selectedProductId === null) return;
    const params = new URLSearchParams(searchParams);
    startTransition(() => {
      params.delete('productId');
      router.replace(`?${params.toString()}`, { scroll: false });
    });
  }, [selectedProductId, searchParams, router]);

  const closeItemFilter = useCallback(() => setIsItemFilterOpen(false), []);

  return (
    <>
      {/* SP: 作品概要 */}
      <div className={utilsStyles.onlySp}>
        <div className={`${layoutStyles.gfPageContainerLarge}`}>
          <div className={styles.spWorkCard}>
            <div className={styles.spWorkImage}>
              <NextImageView
                src={work.imagePath}
                alt={work.name}
                fill
                sizes={getImageLayoutSizes({ sp: '25vw', pc: '0' })}
                objectFit='contain'
              />
            </div>
            <div className={styles.spWorkMeta}>
              <h2 className={styles.spWorkName}>{work.name}</h2>
              <p className={styles.spCreatorName}>{work.creators.map((c) => c.name).join('、')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 2カラムレイアウト */}
      <div className={`${layoutStyles.gfPageContainerLarge} ${styles.pageLayout}`}>
        {/* PC: 左サイドバー（作品情報のみ。フィルターはボタン→ドロワーに統一） */}
        <WorkDetailSidebar work={work} />

        {/* メインコンテンツ */}
        <div className={styles.mainContent}>
          <UserDesignGridSection
            designs={initialDesigns}
            hasMore={initialHasMore}
            // TODO: dev環境でのビジネスサイドのデバッグ用に無限スクロールが発生するようにしているが、リリースする際はもう少し増やす
            apiParams={{ gfWorkId: workId, gfProductId: selectedProductId ?? undefined, limit: 30 }}
            variant='work'
            callbackUrlBase={`/works/${workId}/user-designs`}
            sizeSelectFrom={SIZE_SELECT_FROM.userDesigns}
            onOpenItemFilter={() => setIsItemFilterOpen(true)}
            selectedProductName={selectedProduct?.name}
            onRemoveItemFilter={handleRemoveItemFilter}
            isFilterPending={isPending}
            initialOpenUserPrintId={initialOpenUserPrintId}
          />
        </div>
      </div>

      {/* SP: 作品情報フッター */}
      <div className={utilsStyles.onlySp}>
        <div className={layoutStyles.gfPageContainerLarge}>
          <WorksDetailFooterProductFirst work={work} />
        </div>
      </div>

      {/* アイテムフィルターモーダル */}
      <ItemFilterModal
        isOpen={isItemFilterOpen}
        onClose={closeItemFilter}
        products={products}
        selectedProductIds={selectedProductIds}
        onToggleProduct={handleItemFilterToggle}
        onClear={handleRemoveItemFilter}
      />
    </>
  );
}

export default WorkUserDesignsPage;
